import React, { ReactNode, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, RefreshCw, Wifi } from 'lucide-react';
import { LoadingProvider } from '../contexts/LoadingContext';
import { usePageLoadDetection } from '../hooks/usePageLoadDetection';

interface Props {
  children: ReactNode;
  fallback: ReactNode;
  timeout?: number;
}

function SuspenseWithTimeout({ children, fallback, timeout = 120000 }: Props) {
  const location = useLocation();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const startTimeRef = useRef(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevPathnameRef = useRef(location.pathname);

  const clearAllTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const startTimeouts = useCallback(() => {
    clearAllTimeouts();
    startTimeRef.current = Date.now();

    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, 60000);

    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true);
    }, timeout);
  }, [clearAllTimeouts, timeout]);

  useEffect(() => {
    startTimeouts();
    return clearAllTimeouts;
  }, [startTimeouts, clearAllTimeouts]);

  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      setIsLoaded(false);
      setHasTimedOut(false);
      setShowWarning(false);
      startTimeouts();
    }
  }, [location.pathname, startTimeouts]);

  const notifyLoaded = useCallback(() => {
    if (!isLoaded && !hasTimedOut) {
      setIsLoaded(true);
      setShowWarning(false);
      clearAllTimeouts();

      const loadTime = Date.now() - startTimeRef.current;
      if (loadTime > 3000) {
        console.log(`Page loaded after ${(loadTime / 1000).toFixed(1)}s`);
      }
    }
  }, [isLoaded, hasTimedOut, clearAllTimeouts]);

  usePageLoadDetection({
    onLoadDetected: notifyLoaded,
    enabled: !isLoaded && !hasTimedOut,
    sensitivity: 'balanced'
  });

  const handleReload = () => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  };

  const handleRetry = () => {
    setHasTimedOut(false);
    setShowWarning(false);
    setIsLoaded(false);
    startTimeouts();
  };

  if (hasTimedOut) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-cyan-50/20 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-red-100 rounded-full p-3">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>

          <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Loading Failed
          </h1>

          <p className="text-gray-600 text-center mb-6">
            This page failed to load after 120 seconds. This may indicate a network issue or server problem.
          </p>

          <div className="space-y-2">
            <button
              onClick={handleRetry}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </button>

            <button
              onClick={handleReload}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Reload Page
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            If this problem persists, please check your internet connection or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showWarning && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4 flex items-start space-x-3">
            <Wifi className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">
                Slow Connection Detected
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                The page is taking longer than usual to load. Please wait...
              </p>
            </div>
          </div>
        </div>
      )}
      <LoadingProvider value={{ notifyLoaded }}>
        <Suspense fallback={fallback}>
          {children}
        </Suspense>
      </LoadingProvider>
    </>
  );
}

export default SuspenseWithTimeout;
